import { collection, addDoc, query, orderBy, limit, getDocs, where, updateDoc, doc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import DOMPurify from 'dompurify';
import { db, storage } from '../firebase';
import { NewPostFormValues, Post } from '../types/blog';
import { getAuth } from 'firebase/auth';

// Hàm chuyển base64 thành Blob
const base64ToBlob = (base64: string, mime: string): Blob => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mime });
};

// Hàm upload hình ảnh từ content (HTML với base64)
const uploadImagesFromContent = async (html: string): Promise<string> => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const images = doc.querySelectorAll('img');
    const uploadPromises: Promise<string>[] = [];

    images.forEach((img) => {
        const src = img.getAttribute('src');
        if (src && src.startsWith('data:image/')) {
            const matchResult = src.match(/^data:(image\/[^;]+);base64,(.+)$/);
            const [, mime = '', base64 = ''] = matchResult || [];
            if (mime && base64) {
                const blob = base64ToBlob(base64, mime);
                const fileName = `blog_images/${Date.now()}_${Math.random().toString(36).substring(7)}.${mime.split('/')[1]}`;
                const storageRef = ref(storage, fileName);

                const uploadTask = uploadBytesResumable(storageRef, blob);
                const promise = new Promise<string>((resolve, reject) => {
                    uploadTask.on(
                        'state_changed',
                        () => { }, // Progress
                        reject,
                        () => getDownloadURL(uploadTask.snapshot.ref).then(resolve).catch(reject)
                    );
                });
                uploadPromises.push(promise.then((url) => {
                    img.setAttribute('src', url);
                    return url;
                }));
            }
        }
    });

    await Promise.all(uploadPromises);
    return DOMPurify.sanitize(doc.body.innerHTML);
};

// Hàm upload cover image
const uploadCover = async (cover: { name: string; preview: string; path: string } | null): Promise<string> => {
    if (!cover || !cover.preview) {
        throw new Error('No cover image provided');
    }

    const response = await fetch(cover.preview);
    const blob = await response.blob();

    // Lấy tên file từ cover.path hoặc cover.name
    let fileName = cover.path || cover.name || `cover_${Date.now()}`;

    // Xử lý chuỗi: bỏ ./ ở đầu và extension
    fileName = fileName.replace(/^\.\//, ''); // Bỏ ./ ở đầu

    // Nếu fileName rỗng, fallback sang timestamp
    if (!fileName) {
        fileName = `cover_${Date.now()}`;
    }
    if (!fileName.includes('.')) {
        fileName += `.${blob.type.split('/')[1] || 'jpg'}`;
    }

    const fullPath = `blog_covers/${fileName}`;
    const storageRef = ref(storage, fullPath);
    const uploadTask = uploadBytesResumable(storageRef, blob);

    return new Promise((resolve, reject) => {
        uploadTask.on(
            'state_changed',
            (snapshot) => {
            },
            (error) => reject(error),
            () => getDownloadURL(uploadTask.snapshot.ref).then(resolve).catch(reject)
        );
    });
};

export function generateSlug(title: string): string {
    if (!title) return "";

    // 1️⃣ Chuẩn hóa tiếng Việt: bỏ dấu
    let slug = title.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    // 2️⃣ Chuyển hết về chữ thường
    slug = slug.toLowerCase();

    // 3️⃣ Thay ký tự đặc biệt, khoảng trắng bằng dấu gạch ngang
    slug = slug.replace(/[^a-z0-9\s-]/g, ""); // chỉ giữ chữ, số, khoảng trắng, dấu -
    slug = slug.replace(/\s+/g, "-"); // thay khoảng trắng bằng -
    slug = slug.replace(/-+/g, "-"); // bỏ gạch ngang trùng nhau

    // 4️⃣ Loại bỏ gạch đầu/cuối
    slug = slug.replace(/^-+|-+$/g, "");

    return slug;
}

// Hàm chính để lưu bài viết
export const saveBlogPostToFirebase = async (values: NewPostFormValues): Promise<string> => {
    const auth = getAuth();
    if (!auth.currentUser) {
        throw new Error('User must be authenticated to save blog');
    }
    try {
        // Upload images trong content
        const updatedContent = await uploadImagesFromContent(values.content);

        // Upload cover
        let coverUrl = '';
        if (values.cover) {
            coverUrl = await uploadCover(values.cover);
        }

        // Chuẩn bị dữ liệu để lưu
        const blogData = {
            ...values,
            content: updatedContent,
            cover: coverUrl,
            createdAt: new Date().toISOString(),
        };

        // Lưu vào Firestore
        const docRef = await addDoc(collection(db, 'blogs'), blogData);
        return docRef.id;
    } catch (error) {
        throw new Error('Failed to save blog post: ' + (error as Error).message);
    }
};

export const updateBlogPostInFirebase = async (
    postId: string,
    values: NewPostFormValues
): Promise<boolean> => {
    const auth = getAuth();
    if (!auth.currentUser) {
        throw new Error("User must be authenticated to update blog");
    }

    try {
        // Upload lại ảnh trong content nếu có
        const updatedContent = await uploadImagesFromContent(values.content);

        // Upload lại cover nếu có file mới
        let coverUrl = values.cover as string;
        if (values.cover && typeof values.cover !== "string") {
            coverUrl = await uploadCover(values.cover);
        }

        // Chuẩn bị dữ liệu cập nhật
        const updatedData = {
            ...values,
            content: updatedContent,
            cover: coverUrl,
            updatedAt: new Date().toISOString(),
        };

        // Cập nhật Firestore
        const postRef = doc(db, "blogs", postId);
        await updateDoc(postRef, updatedData);

        // ✅ Thành công
        return true;
    } catch (error) {
        console.error("Update failed:", error);
        throw new Error("Failed to update blog post: " + (error as Error).message);
    }
};

export const getAllBlogPosts = async (): Promise<Post[]> => {
    try {
        const blogsRef = collection(db, 'blogs');
        const q = query(blogsRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);

        const posts: Post[] = [];
        querySnapshot.forEach((doc) => {
            const data = doc.data();
            posts.push({
                id: doc.id,
                slug: data.slug || "",
                title: data.title || '',
                body: data.content || '', // Map content -> body
                cover: data.cover || '',
                description: data.description || '',
                createdAt: data.createdAt || new Date().toISOString(),
                view: data.view || 0,
                comment: data.comment || 0,
                share: data.share || 0,
                favorite: data.favorite || 0,
                author: data.author || { name: 'Unknown', avatarUrl: '' },
                tags: data.tags || [],
                favoritePerson: data.favoritePerson || [],
                comments: data.comments || [],
            });
        });

        return posts;
    } catch (error) {
        console.error('Error fetching blog posts:', error);
        throw new Error('Failed to fetch blog posts: ' + (error as Error).message);
    }
};

export const getPostByTitle = async (title: string): Promise<Post> => {
    try {
        const blogsRef = collection(db, 'blogs');
        const q = query(blogsRef, where('title', '==', title), limit(1));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            throw new Error('Post not found');
        }

        const doc = querySnapshot.docs[0];
        const data = doc.data();
        return {
            id: doc.id,
            slug: data.slug,
            title: data.title || '',
            body: data.content || '',
            cover: data.cover || '',
            description: data.description || '',
            createdAt: data.createdAt || new Date().toISOString(),
            view: data.view || 0,
            comment: data.comment || 0,
            share: data.share || 0,
            favorite: data.favorite || 0,
            author: data.author || { name: 'Unknown', avatarUrl: '' },
            tags: data.tags || [],
            favoritePerson: data.favoritePerson || [],
            comments: data.comments || [],
        };
    } catch (error) {
        throw new Error('Failed to fetch post: ' + (error as Error).message);
    }
};

export const getPostfromFirebaseByID = async (id: string): Promise<Post> => {
    try {
        const blogsRef = collection(db, 'blogs');
        const q = query(blogsRef, where('id', '==', id), limit(1));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            throw new Error('Post not found');
        }

        const doc = querySnapshot.docs[0];
        const data = doc.data();
        return {
            id: doc.id,
            slug: data.slug,
            title: data.title || '',
            body: data.content || '',
            cover: data.cover || '',
            description: data.description || '',
            createdAt: data.createdAt || new Date().toISOString(),
            view: data.view || 0,
            comment: data.comment || 0,
            share: data.share || 0,
            favorite: data.favorite || 0,
            author: data.author || { name: 'Unknown', avatarUrl: '' },
            tags: data.tags || [],
            favoritePerson: data.favoritePerson || [],
            comments: data.comments || [],
        };
    } catch (error) {
        throw new Error('Failed to fetch post: ' + (error as Error).message);
    }
};

export const getRecentPostsFromFirebase = async (excludeTitle: string): Promise<Post[]> => {
    try {
        const blogsRef = collection(db, 'blogs');
        const q = query(blogsRef, orderBy('createdAt', 'desc'), limit(5));
        const querySnapshot = await getDocs(q);

        const posts: Post[] = [];
        querySnapshot.forEach((doc) => {
            const data = doc.data();
            if (data.title !== excludeTitle) {
                posts.push({
                    id: doc.id,
                    slug: data.slug || '',
                    title: data.title || '',
                    body: data.content || '',
                    cover: data.cover || '',
                    description: data.description || '',
                    createdAt: data.createdAt || new Date().toISOString(),
                    view: data.view || 0,
                    comment: data.comment || 0,
                    share: data.share || 0,
                    favorite: data.favorite || 0,
                    author: data.author || { name: 'Unknown', avatarUrl: '' },
                    tags: data.tags || [],
                    favoritePerson: data.favoritePerson || [],
                    comments: data.comments || [],
                });
            }
        });

        return posts;
    } catch (error) {
        throw new Error('Failed to fetch recent posts: ' + (error as Error).message);
    }
};

export const getInitialPosts = async (index: number, step: number): Promise<Post[]> => {
    try {
        const blogsRef = collection(db, 'blogs');
        const q = query(blogsRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);

        const posts: Post[] = [];
        querySnapshot.forEach((doc) => {
            const data = doc.data();
            posts.push({
                id: doc.id,
                slug: data.slug,
                title: data.title || '',
                body: data.content || '', // Map content -> body
                cover: data.cover || '',
                description: data.description || '',
                createdAt: data.createdAt || new Date().toISOString(),
                view: data.view || 0,
                comment: data.comment || 0,
                share: data.share || 0,
                favorite: data.favorite || 0,
                author: data.author || { name: 'Unknown', avatarUrl: '' },
                tags: data.tags || [],
                favoritePerson: data.favoritePerson || [],
                comments: data.comments || [],
            });
        });

        return posts;
    } catch (error) {
        console.error('Error fetching blog posts:', error);
        throw new Error('Failed to fetch blog posts: ' + (error as Error).message);
    }
};